import { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { criticalDataScope } from "../redux/api/criticalDataContract";
import {
  fetchAllCriticalData,
  refreshCriticalData,
  selectCriticalDataError,
  selectGlobalLoading,
  selectInitialized,
  selectAllCriticalData,
  clearError,
} from "../redux/slices/criticalDataSlice";
import { selectDropdownLoading, selectDropdownError, fetchDropdownValues } from "../redux/slices/dropdownSlice";

const AUTO_RETRY_COOLDOWN_MS = 3000;

/**
 * Custom hook to manage critical API data loading with caching and automatic refresh
 * 
 * This hook provides a centralized way to fetch and manage critical application data
 * including hover descriptions, global variables, TBD values, holidays, user preferences,
 * icon data, and user dropdowns.
 * 
 * Features:
 * - Automatic loading on mount with caching
 * - Per-API loading states and error handling
 * - Manual refresh capabilities
 * - MSAL integration for user dropdown fetching
 * 
 * @param {Object} options - Configuration options
 * @param {Array<string>} options.apiKeys - Optional array of specific API keys to load
 * @param {boolean} options.autoLoad - Whether to automatically load data on mount (default: true)
 * @param {boolean} options.forceRefresh - Force refresh on mount even if cached (default: false)
 * @param {boolean} options.includeUserDropdowns - Whether to fetch user dropdowns (default: true)
 * 
 * @returns {Object} Hook utilities and state
 * @returns {Function} loadCriticalData - Function to manually load all critical data
 * @returns {Function} loadSpecificApi - Function to load a specific API by key
 * @returns {Function} refreshApi - Function to force refresh a specific API
 * @returns {Function} clearApiError - Function to clear error for a specific API
 * @returns {Object} data - Object containing all critical data by API key
 * @returns {boolean} isLoading - Global loading state (true if any API is loading)
 * @returns {boolean} initialized - Whether initial data load is complete
 * @returns {Object} loadingStates - Per-API loading states
 * @returns {Object} errors - Per-API error states
 * 
 * @example
 * // Basic usage - auto-loads all critical data on mount
 * const { data, isLoading, initialized } = useCriticalData();
 * 
 * @example
 * // Load specific APIs only
 * const { data, loadCriticalData } = useCriticalData({ 
 *   apiKeys: ['globalVariables', 'holidays'],
 *   autoLoad: false 
 * });
 * 
 * // Manually trigger load
 * useEffect(() => {
 *   loadCriticalData();
 * }, []);
 * 
 * @example
 * // With manual refresh
 * const { refreshApi, data } = useCriticalData();
 * 
 * const handleUserAction = async () => {
 *   await refreshApi('tbdValues'); // Force refresh TBD values
 *   console.log('Fresh TBD data:', data.tbdValues);
 * };
 * 
 * @example
 * // Access specific data
 * const { data, isLoading } = useCriticalData();
 * 
 * if (isLoading) return <Spinner />;
 * 
 * const holidays = data.holidays;
 * const globalVars = data.globalVariables;
 */
const useCriticalData = ({
  apiKeys = null,
  autoLoad = true,
  forceRefresh = false,
  includeUserDropdowns = true,
} = {}) => {
  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();
  const currentAccount = useSelector(state => state.user?.userDetails?.localAccountId);
  const scope = useSelector(criticalDataScope);
  const authenticated = Boolean(currentAccount && currentAccount === accounts[0]?.localAccountId);
  const hasLoadedRef = useRef(false);
  const lastAttemptAtRef = useRef(0);

  // Select all critical data from Redux
  const allData = useSelector(selectAllCriticalData);
  const globalLoading = useSelector(selectGlobalLoading);
  const initialized = useSelector(selectInitialized);
  const criticalError = useSelector(selectCriticalDataError);
  const legacyDropdownCount = useSelector((state) => state.dropdown?.options?.length || 0);

  // Critical data slice has global loading/error states, not per-API
  // All critical data is loaded together from unified endpoint
  const apiKeysToLoad = apiKeys || [
    'dropdownValues',
    'hoverDescriptions',
    'globalVariables',
    'tbdAudit',
    'holidays',
    'userPreferences',
    'iconData',
  ];

  // Since critical data loads as one unit, loading state is the same for all
  const loadingStates = {};
  const errors = {};

  apiKeysToLoad.forEach(key => {
    loadingStates[key] = globalLoading;
    errors[key] = criticalError;
  });

  // Add user dropdowns loading and error states if requested
  const dropdownLoading = useSelector(selectDropdownLoading);
  const dropdownError = useSelector(selectDropdownError);
  
  if (includeUserDropdowns) {
    loadingStates.userDropdowns = dropdownLoading;
    errors.userDropdowns = dropdownError;
  }

  /**
   * Load all critical data
   * @param {boolean} force - Force refresh even if cached
   */
  const loadCriticalData = useCallback(async (force = forceRefresh) => {
    if (!authenticated) return;
    lastAttemptAtRef.current = Date.now();

    try {
      const options = {
        apiKeys: apiKeys,
        forceRefresh: force,
      };

      // Include MSAL instance for user dropdowns if available and requested
      if (includeUserDropdowns && instance && accounts && accounts.length > 0) {
        options.msalInstance = instance;
        options.msalAccounts = accounts;
      } else if (includeUserDropdowns) {
        console.warn('useCriticalData: User dropdowns requested but MSAL not available', {
          hasInstance: !!instance,
          hasAccounts: !!accounts,
          accountsLength: accounts?.length
        });
      }

      await dispatch(fetchAllCriticalData(options)).unwrap();
    } catch (error) {
      console.error('Failed to load critical data:', error);
      // Allow retry effects to re-attempt after cooldown.
      hasLoadedRef.current = false;
      // Don't throw - let components handle errors via error states
    }
  }, [dispatch, apiKeys, forceRefresh, includeUserDropdowns, instance, accounts, authenticated]);

  /**
   * Load a specific API
   * @param {string} apiKey - The API key to load
   * @param {boolean} force - Force refresh even if cached
   */
  const loadSpecificApi = useCallback(async (apiKey, force = false) => {
    if (!authenticated) return;
    try {
      if (force) await dispatch(refreshCriticalData(apiKey)).unwrap();
      else await dispatch(fetchAllCriticalData()).unwrap();
    } catch (error) {
      console.error(`Failed to load ${apiKey}:`, error);
    }
  }, [dispatch, authenticated]);

  /**
   * Force refresh a specific API
   * @param {string} apiKey - The API key to refresh
   */
  const refreshApi = useCallback(async (apiKey) => {
    try {
      await dispatch(refreshCriticalData(apiKey)).unwrap();
    } catch (error) {
      console.error(`Failed to refresh ${apiKey}:`, error);
    }
  }, [dispatch]);

  /**
   * Clear error for a specific API
   * @param {string} apiKey - The API key to clear error for
   */
  const clearApiError = useCallback((apiKey) => {
    dispatch(clearError({ apiKey }));
  }, [dispatch]);

  /**
   * Check if a specific API has data
   * @param {string} apiKey - The API key to check
   * @returns {boolean} True if data exists
   */
  const hasData = useCallback((apiKey) => {
    const data = allData[apiKey];
    if (Array.isArray(data)) {
      return data.length > 0;
    }
    return data !== null && data !== undefined;
  }, [allData]);

  /**
   * Get specific API data
   * @param {string} apiKey - The API key to get data for
   * @returns {any} The data for the specified API
   */
  const getData = useCallback((apiKey) => {
    return allData[apiKey];
  }, [allData]);

  // A forced mount refresh is one-shot per session, not per successful response.
  useEffect(() => {
    hasLoadedRef.current = false;
    lastAttemptAtRef.current = 0;
  }, [scope]);

  // Auto-load on mount if enabled
  useEffect(() => {
    const shouldLoad = forceRefresh || !initialized;
    const cooldownElapsed = Date.now() - lastAttemptAtRef.current >= AUTO_RETRY_COOLDOWN_MS;

    if (authenticated && autoLoad && !hasLoadedRef.current && shouldLoad && cooldownElapsed) {
      hasLoadedRef.current = true;
      loadCriticalData();
    }
  }, [authenticated, autoLoad, forceRefresh, initialized, globalLoading, loadCriticalData]);

  // Backward-compat hydration: many components still read state.dropdown.options.
  // If criticalData has dropdownValues but legacy dropdown options were cleared,
  // repopulate from cache without forcing a network request.
  useEffect(() => {
    const criticalDropdownValues = allData?.dropdownValues;
    if (Array.isArray(criticalDropdownValues) && criticalDropdownValues.length > 0 && legacyDropdownCount === 0) {
      dispatch(fetchDropdownValues());
    }
  }, [dispatch, allData, legacyDropdownCount]);

  // Compute overall loading state
  // Note: Don't include dropdownLoading in overall loading state as it causes infinite loops
  // Dropdowns load separately with their own cache and components can check loadingStates.userDropdowns if needed
  const overallLoading = globalLoading;

  return {
    // Functions
    loadCriticalData,
    loadSpecificApi,
    refreshApi,
    clearApiError,
    hasData,
    getData,

    // State
    data: allData,
    isLoading: overallLoading,
    initialized,
    loadingStates,
    errors,

    // Convenience accessors for specific APIs (matching criticalDataSlice structure)
    dropdownValues: allData.dropdownValues,
    hoverDescriptions: allData.hoverDescriptions,
    globalVariables: allData.globalVariables,
    tbdAudit: allData.tbdAudit,
    holidays: allData.holidays,
    userPreferences: allData.userPreferences,
    iconData: allData.iconData,
    // Backward compatibility alias
    tbdValues: allData.tbdAudit,
  };
};

export default useCriticalData;
